/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  For,
  type JSX,
  Show,
  createSignal,
  lazy,
  onCleanup,
  onMount,
} from "solid-js";
import { Portal } from "solid-js/web";

import { useWatch } from "@/hooks/useWatch";
import { pubSub } from "@/lib/PubSub.ts";
import { isMobile } from "@/lib/isMobile.ts";
import { routerStore } from "@/stores/routerStore.ts";
import { PubSubEvents } from "@/types/PubSub.ts";

import { DialogProvider } from "./DialogContext";
import { DialogType } from "./types.ts";

const dialogComponents = {
  [DialogType.Salesforce]: lazy(() => import("./Salesforce")),
  [DialogType.AffiliateInvitationChest]: lazy(
    () => import("./affiliate/InvitationChest"),
  ),
  [DialogType.AffiliateCommissionHistory]: lazy(
    () => import("./affiliate/CommissionHistory"),
  ),
  [DialogType.AffiliateShare]: lazy(() => import("./affiliate/Share")),
  [DialogType.TeamExplain]: lazy(
    () => import("./affiliate/TeamAgent/explain.tsx"),
  ),
  [DialogType.TeamDetails]: lazy(() => import("./affiliate/TeamAgent/Details")),
  [DialogType.TeamInvitee]: lazy(() => import("./affiliate/TeamAgent/invitee")),
  [DialogType.AffiliateLockedRewards]: lazy(
    () => import("./affiliate/LockedRewards"),
  ),
  [DialogType.AffiliateLockedVipRewards]: lazy(
    () => import("./affiliate/LockedVipRewards"),
  ),
  [DialogType.Tips]: lazy(() => import("./Tips/Tips")),
  // 语言选择
  [DialogType.SwitchLanguage]: lazy(
    () => import("./SwitchLanguage/SwitchLanguage"),
  ),
  [DialogType.ChangeInfo]: lazy(() => import("./ChangeSetting/ChangeInfo")),
  [DialogType.VerifyCode]: lazy(() => import("./ChangeSetting/VerifyCode")),
  // 设置 - 验证 - 个人信息
  [DialogType.PersonalInfo]: lazy(() => import("./ChangeSetting/PersonalInfo")),
  [DialogType.ChangePwd]: lazy(() => import("./ChangeSetting/ChangePwd")),
  [DialogType.NoCodeReceived]: lazy(
    () => import("./ChangeSetting/NoCodeReceived"),
  ),
  [DialogType.BillFlow]: lazy(() =>
    isMobile()
      ? import("./BillFlow/BillFlowH5.tsx")
      : import("./BillFlow/BillFlowPC.tsx"),
  ),
  [DialogType.BillFlowDetail]: lazy(() =>
    isMobile()
      ? import("./BillFlow/BillFlowDetailH5.tsx")
      : import("./BillFlow/BillFlowDetailPC.tsx"),
  ),
  [DialogType.BindDefaultPoint]: lazy(() => import("./BindDefaultPoint")),
  // 提现 - 二级弹窗
  [DialogType.WithdrawEnter]: lazy(
    () => import("./Withdraw/WithdrawConfirm.tsx"),
  ),

  // 提现
  [DialogType.Withdraw]: lazy(() =>
    import("@/pages/pc/wallet/withdraw").then((mod) => ({
      default: mod.WithdrawDialog,
    })),
  ),
  [DialogType.AccessInstructions]: lazy(
    () => import("./Wallet/AccessInstructions.tsx"),
  ),
  [DialogType.DepositProgress]: lazy(() => import("./Deposit/DepositProgress")),
  [DialogType.RepeatOrders]: lazy(() => import("./Deposit/RepeatOrders")),
  [DialogType.SpeedUpDeposits]: lazy(() => import("./Deposit/SpeedUpDeposits")),
  [DialogType.DetailsRules]: lazy(() => import("./Deposit/DetailsRules")),
  [DialogType.DepositOrderDetails]: lazy(
    () => import("./Deposit/DepositOrderDetails"),
  ),
  // 个人中心
  [DialogType.Profile]: lazy(() => import("./MyProfile/Profile")),
  [DialogType.EditProfile]: lazy(() => import("./MyProfile/EditProfile")),
  [DialogType.WalletVideo]: lazy(() => import("./Wallet/WalletVideo.tsx")),
  [DialogType.PendingBillFlow]: lazy(
    () => import("./PendingBillFlow/index.tsx"),
  ),
  [DialogType.OrderDetail]: lazy(() => import("./Wallet/OrderDetail.tsx")),
  [DialogType.DepositPayment]: lazy(() => import("./Deposit/DepositPayment")),
  [DialogType.RetainPopup]: lazy(() => import("./Register/RetainPopup")),
  [DialogType.Minors18Popup]: lazy(() => import("./Minors18/Minors18Popup")),
  [DialogType.EmailSucceed]: lazy(() => import("./Reset/EmailSucceed")),
  //
  [DialogType.NoCode]: lazy(() => import("./Reset/NoCode")),
  // 银行卡
  [DialogType.BankCard]: lazy(() => import("./BankCark/index.tsx")),
  // 添加银行卡
  [DialogType.BankCardAdd]: lazy(() => import("./Withdraw/BankCardAdd.tsx")),
  [DialogType.FacialRecognition]: lazy(
    () => import("./Withdraw/FacialRecognition.tsx"),
  ),
  // 资金锁定
  [DialogType.AmountCode]: lazy(() => import("./Wallet/AmountCode.tsx")),
  // 分享
  [DialogType.Share]: lazy(() => import("./Share/share.tsx")),
  // 登出
  [DialogType.SignOut]: lazy(() => import("./SignOut")),
  // 下载App活动
  [DialogType.AppDownloadActivity]: lazy(() => import("./AppDownloadActivity")),
  // 系统运营弹窗
  [DialogType.SystemAndClient]: lazy(
    () => import("./SystemAndClient/Index.tsx"),
  ),
  // 奖励/道具弹窗
  [DialogType.Reward]: lazy(() => import("./Reward/index.tsx")),
  [DialogType.HistoryPopup]: lazy(
    () => import("./WagerContest/HistoryPopup.tsx"),
  ),

  [DialogType.VipLevelUp]: lazy(() => import("./Vip/VipLevelUp.tsx")),
  [DialogType.VipActRules]: lazy(() => import("./Vip/VipActRules.tsx")),
  [DialogType.H5RulePopup]: lazy(
    () => import("./WagerContest/H5RulePopup.tsx"),
  ),

  // 奖金中心 - 普通兑换规则
  [DialogType.RedeemRules]: lazy(() => import("./Bonus/RedeemRules.tsx")),
  // 奖金中心 - 可兑换积分说明
  [DialogType.WagerProfitRules]: lazy(
    () => import("./Bonus/WagerProfitRules.tsx"),
  ),
  // 奖金中心 - 特色奖金弹窗 - Special Bonus
  [DialogType.SpecialBonus]: lazy(() => import("./Bonus/SpecialBonus.tsx")),
  // 奖金中心 - 奖金详情
  [DialogType.BonusDetail]: lazy(() => import("./Bonus/BonusDetail.tsx")),
  // 奖金中心 - 奖金详情 - 全部奖励
  [DialogType.BonusDetailAllReward]: lazy(
    () => import("./Bonus/DetailsAllReward.tsx"),
  ),
  // 安装app引导
  [DialogType.InstallAppGuide]: lazy(
    () => import("./AppDownloadActivity/Guide/index.tsx"),
  ),

  // 扭蛋活动
  [DialogType.GachaActivity]: lazy(() => import("./GachaActivity/index.tsx")),
  // 扭蛋任务列表
  [DialogType.TaskList]: lazy(
    () => import("./GachaActivity/TaskDialog/index.tsx"),
  ),
  // 扭蛋奖励
  [DialogType.GachaRewards]: lazy(
    () =>
      import("@/components/Dialogs/GachaActivity/GachaRewardDialog/index.tsx"),
  ),
  // 救济金合并
  [DialogType.FreeRelief]: lazy(() => import("./FreeRelief/FreeRelief.tsx")),
  // 新人转盘
  [DialogType.NewbieWheel]: lazy(() => import("./Activity/NewbieWheel.tsx")),
  // 新人礼包 超值礼盒
  [DialogType.SuperValueGift]: lazy(() => import("./SuperValueGift/index.tsx")),
  // 新人礼包 超值礼盒 倒计时
  [DialogType.CountDown]: lazy(
    () => import("./SuperValueGift/CountDownDialog/index.tsx"),
  ),
  // 7天签到 start
  [DialogType.NewCheckIn]: lazy(
    () => import("./Activity/NewCheckIn/NewCheckIn.tsx"),
  ),
  [DialogType.NewCheckInTip]: lazy(
    () => import("./Activity/NewCheckIn/NewCheckInTip.tsx"),
  ),
  [DialogType.NewCheckInPropsTip]: lazy(
    () => import("./Activity/NewCheckIn/NewCheckInPropsTip.tsx"),
  ),
  // 7天签到 end
  // 弹窗/路由弹窗 start
  // Deposit
  [DialogType.Deposit]: lazy(() =>
    import("@/pages/pc/wallet/deposit").then((mod) => ({
      default: mod.DepositDialog,
    })),
  ),
  // 奖金中心 - 道具/活动 Rule
  [DialogType.Rule]: lazy(() =>
    isMobile()
      ? import("@/components/H5/Modals/ActRule").then((mod) => ({
          default: mod.ActRulePopupDialog,
        }))
      : import("@/components/PC/Modals/ActRule").then((mod) => ({
          default: mod.ActRulePopupDialog,
        })),
  ),
  // 道具列表 start
  [DialogType.PropsList]: lazy(
    () => import("@/pages/pc/bonuscenter/propsList/index"),
  ),
  [DialogType.FreeGame]: lazy(() => import("./PropsList/FreeGame.tsx")),
  [DialogType.VipExperience]: lazy(
    () => import("./PropsList/VipExperience.tsx"),
  ),
  // 道具列表 end
  // 弹窗/路由弹窗 end
  // 全局搜索
  [DialogType.Search]: lazy(
    () => import("@/components/PC/Components/search/GlobalSearch"),
  ),
  // 检查身份 (註冊 / kyc / 負責任博彩)
  [DialogType.CheckIdentity]: lazy(() => import("./CheckIdentity/index.tsx")),
  // KYC验证首页
  [DialogType.KYC]: lazy(() => import("./KYC/index.tsx")),
  // KYC iframe
  [DialogType.KycIframe]: lazy(() => import("./KYC/iframe.tsx")),

  // SEO 组件弹窗
  [DialogType.Seo]: lazy(() => import("./Seo/index.tsx")),

  // Activity Iframe
  [DialogType.ActivityIframe]: lazy(() => import("./ActivityIframe/index.tsx")),
  [DialogType.DepositIframe]: lazy(() => import("./Wallet/DepositIframe.tsx")),
  // 充值Detail弹窗
  [DialogType.DepositDetail]: lazy(
    () => import("@/components/Dialogs/Deposit/DepositDetail.tsx"),
  ),
  // 锦标赛详情
  [DialogType.TournamentDetail]: lazy(() => import("./Tournament/index.tsx")),
  // 活动轮播图
  [DialogType.ActivityCarousel]: lazy(
    () => import("./ActivityCarousel/index.tsx"),
  ),
} as const;
// 弹窗数据
export const [dialogList, setDialogList] = createSignal<
  Array<{
    id: string; // 添加唯一标识符
    type: DialogType;
    component: () => JSX.Element;
    data: any;
    zIndex: number;
  }>
>([]);

// z-index 计数器，从 4000 开始
let zIndexCounter = 4000;

// 记录所有已弹出的 dialog 的 zIndex（包括已关闭的）
// TODO: 路由Dialog 3999/Dialog 4000/Popup 10001/ActRulePopup 8000/Toast 10000/Message 10000共享zIndex
const dialogZIndexHistory = new Set<number>();

// 获取下一个可用的 zIndex
export const getNextZIndex = (specifiedZIndex?: number): number => {
  if (specifiedZIndex !== undefined) {
    // 如果指定了 zIndex，直接使用
    dialogZIndexHistory.add(specifiedZIndex);
    return specifiedZIndex;
  }

  // 从当前最高 zIndex 开始递增
  let nextZIndex = zIndexCounter;
  while (dialogZIndexHistory.has(nextZIndex)) {
    nextZIndex++;
  }

  // 更新计数器并记录
  zIndexCounter = nextZIndex;
  dialogZIndexHistory.add(nextZIndex);

  return nextZIndex;
};

// 检查是否需要重置 zIndex 状态
export const checkAndResetZIndex = () => {
  if (dialogList().length === 0 && !routerStore.currentRoute) {
    // 所有弹窗都关闭了，重置为基准值
    zIndexCounter = 4000;
    dialogZIndexHistory.clear();
  }
};

// 监听路由弹窗关闭事件，重置zIndex
const excludeKeywords = ["search"]; // 可依需要增減, 因为怕删除监听影响其他内容, 所以才先暂时处理
// https://zendao.baidu.date/bug-view-5237.html 第三个 bug

useWatch(
  () => routerStore.currentRoute,
  () => {
    const path = window.location.pathname;
    /* prettier-ignore */
    const shouldExclude = excludeKeywords.some((keyword) => path.includes(keyword));

    if (!routerStore.currentRoute && !shouldExclude) {
      checkAndResetZIndex();
    }
  },
);
// 调试功能：获取当前 zIndex 状态
export const getZIndexStatus = () => {
  return {
    currentCounter: zIndexCounter,
    usedZIndexes: Array.from(dialogZIndexHistory).sort((a, b) => a - b),
    activeDialogs: dialogList().map((dialog) => ({
      id: dialog.id,
      type: dialog.type,
      zIndex: dialog.zIndex,
    })),
    isReset: dialogList().length === 0,
  };
};

// 调试功能：重置 zIndex 状态（仅用于开发调试）
export const resetZIndexState = () => {
  zIndexCounter = 4000;
  dialogZIndexHistory.clear();
};

export function DialogContainer() {
  console.log("DialogContainer created - setting up subscriptions");

  // 存储回调函数引用以便正确取消订阅
  const showDialogCallback = (props: {
    type: DialogType;
    data?: any;
    zIndex?: number;
  }) => {
    console.log("ShowDialog event received:", props.type);

    // 检查是否已有相同类型的弹窗
    const existingDialog = dialogList().find(
      (dialog) => dialog.type === props.type,
    );
    if (existingDialog) {
      console.log(
        `Dialog of type ${props.type} already exists, ignoring new request`,
      );
      return;
    }

    const DialogComponent = dialogComponents[props.type];
    if (DialogComponent) {
      // 获取下一个可用的 zIndex
      const finalZIndex = getNextZIndex(props.zIndex);

      // 生成唯一ID
      const dialogId = `dialog-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      setDialogList((prev) => [
        ...prev,
        {
          id: dialogId,
          type: props.type,
          component: () => {
            return (
              <DialogComponent
                {...props}
                dialogData={props.data}
                zIndex={finalZIndex}
              />
            );
          },
          data: props.data,
          zIndex: finalZIndex,
        },
      ]);
    }
  };

  const closeDialogCallback = (type: DialogType) => {
    console.log("CloseDialog event received:", type);
    setDialogList((prev) => {
      // 找到要关闭的弹窗（通常是最后添加的同类型弹窗）
      const dialogToClose = prev?.findLast((item) => item.type === type);
      if (!dialogToClose) return prev;

      const newList = prev?.filter((item) => item.id !== dialogToClose.id);

      // 检查是否需要重置 zIndex
      if (newList.length === 0) {
        // 使用 setTimeout 确保状态更新完成后再重置
        setTimeout(() => {
          checkAndResetZIndex();
        }, 0);
      }

      return newList;
    });
  };

  pubSub.subscribe(PubSubEvents.ShowDialog, showDialogCallback);
  pubSub.subscribe(PubSubEvents.CloseDialog, closeDialogCallback);

  onCleanup(() => {
    pubSub.unsubscribe(PubSubEvents.ShowDialog, showDialogCallback);
    pubSub.unsubscribe(PubSubEvents.CloseDialog, closeDialogCallback);
  });

  // 监听浏览器返回键，关闭所有弹窗
  onMount(() => {
    const handlePopState = () => {
      const currentDialogs = dialogList();

      // 如果有打开的弹窗，关闭所有弹窗
      if (currentDialogs.length > 0) {
        setDialogList([]);

        // 重置 zIndex 状态
        checkAndResetZIndex();

        // 阻止默认的后退行为，保持在当前页面
        window.history.pushState(null, "", window.location.href);
      }
    };

    // 添加事件监听
    window.addEventListener("popstate", handlePopState);

    // 组件卸载时移除监听
    onCleanup(() => {
      window.removeEventListener("popstate", handlePopState);
    });
  });

  return (
    <Portal mount={document.body}>
      <Show when={dialogList().length > 0} fallback={<></>}>
        <For each={dialogList()}>
          {(item) => {
            const DialogComponent = dialogComponents[item.type];
            if (!DialogComponent) return null;

            return (
              <DialogProvider dialogType={item.type}>
                <DialogComponent
                  key={item.id}
                  dialogData={item.data}
                  zIndex={item.zIndex}
                />
              </DialogProvider>
            );
          }}
        </For>
      </Show>
    </Portal>
  );
}
